import React from "react";
import { makeStyles } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialogContent-root, .MuiDialogTitle-root": {
      padding: 0,
    },
    // Override MUI's default Paper border-radius (4px) so the card
    // matches the buttons' 8px corners and the wider 8px-design-system
    // pieces in the campaign builder.
    "& .MuiDialog-paper": {
      borderRadius: "8px",
    },
  },
  content: {
    padding: "32px 24px 24px 24px",
    maxWidth: "600px",
  },
  title: {
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    marginBottom: "16px",
    "& > h2": {
      fontWeight: "700",
      fontSize: "22px",
      lineHeight: "26px",
      fontFamily: `"Inter", "Inter", "sans-serif"`,
      color: "#000",
    },
  },
  body: {
    textAlign: "center" as const,
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "22px",
    fontFamily: `"Inter", "Inter", "sans-serif"`,
    margin: 0,
    color: "#555",
  },
  buttonContainer: {
    display: "flex",
    // Buttons hug the opposite edges of the content row so the
    // secondary/primary distinction is visually clear and there's
    // breathing room between them — matches the BrowserWarningModal
    // button layout.
    justifyContent: "space-between",
    width: "100%",
    marginTop: "32px",
  },
  primaryButton: {
    background: "#EE4360",
    border: "1px solid #EE4360",
    borderRadius: "8px",
    // Lock to a 40px-tall hit target; `padding: 0 24px` + flex
    // centering keep the label vertically centered without vertical
    // padding. Matches the DesignFormatChangeModal's button sizing
    // so the two warnings on this page read as one design system.
    height: "40px",
    minHeight: "40px",
    maxHeight: "40px",
    padding: "0 24px",
    boxSizing: "border-box" as const,
    minWidth: "150px",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "20px",
    textTransform: "initial" as const,
    "&:hover": {
      background: "#FFFFFF",
      border: "1px solid #EE4360",
      color: "#EE4360",
    },
  },
  secondaryButton: {
    borderRadius: "8px",
    border: "1px solid #000",
    height: "40px",
    minHeight: "40px",
    maxHeight: "40px",
    padding: "0 24px",
    boxSizing: "border-box" as const,
    minWidth: "150px",
    fontWeight: "bold" as const,
    fontSize: "16px",
    lineHeight: "20px",
    textTransform: "initial" as const,
    background: "#FFFFFF",
    color: "#000",
    "&:hover": {
      background: "#F5F5F5",
    },
  },
});

interface DesignGalleryAddressBoxModalProps {
  open: boolean;
  onClose: () => void;
  onAddAddressBox: () => void;
}

export const DesignGalleryAddressBoxModal: React.FC<DesignGalleryAddressBoxModalProps> = ({
  open,
  onClose,
  onAddAddressBox,
}) => {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing by clicking outside
      aria-labelledby="design-gallery-address-box-dialog"
      className={classes.dialog}
      data-cypress-id={"designGalleryAddressBoxModal"}
    >
      <Box className={classes.content} data-cypress-id={"designGalleryAddressBoxModalContentWrapper"}>
        <DialogContent data-cypress-id={"designGalleryAddressBoxModalContent"}>
          <DialogTitle
            id="design-gallery-address-box-dialog"
            className={classes.title}
            data-cypress-id={"designGalleryAddressBoxModalTitle"}
          >
            Address Box Required
          </DialogTitle>
          <DialogContentText className={classes.body} data-cypress-id={"designGalleryAddressBoxModalBody"}>
            The selected design does not have an address box. An address box is required for Direct Mail campaigns. You
            can add one now or choose a different design.
          </DialogContentText>
        </DialogContent>
        <Box className={classes.buttonContainer} data-cypress-id={"designGalleryAddressBoxModalButtonContainer"}>
          <Button
            className={classes.secondaryButton}
            onClick={onClose}
            data-cypress-id={"designGalleryAddressBoxModalSecondaryButton"}
          >
            Choose Another Design
          </Button>
          <Button
            className={classes.primaryButton}
            onClick={onAddAddressBox}
            data-cypress-id={"designGalleryAddressBoxModalPrimaryButton"}
          >
            Add Address Box
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
